import { Container, Graphics, Sprite, Texture, type Renderer } from 'pixi.js';

import { Icons, type IconName } from './icons.ts';

import { project, type CommunityMarker, type Projection, type SightedCamera } from '@hotlist/shared';

import type { CommunityHeld } from './community.ts';
import type { Coverage, KnownCameras } from './knowncameras.ts';
import type { Viewport } from './viewport.ts';

/**
 * The cameras you have found, drawn where you found them.
 *
 * Small, angular and the same color as an alert, because a camera on this map
 * is not scenery: it is the thing the whole other half of the game is made of,
 * and once you know where one is, every route you plan is planned around it.
 *
 * Nothing is drawn for a camera that has not been seen. That is not a rendering
 * decision, it is the design: section 8 is explicit that coverage is never
 * handed to a client, and the client is not holding any to draw.
 */
/**
 * How wide the cone is, in degrees.
 *
 * Not a measured field of view -- the archive records no lens angle. It is wide
 * enough to read as a direction at a glance and narrow enough that two cameras
 * at one junction do not merge into a single glow.
 */
const CONE_DEGREES = 46;

/** Its length in the texture, which the sprite then scales. */
const CONE_LENGTH = 48;

export class CameraLayer {
  readonly container = new Container();

  /**
   * Three layers, in the order they have to be drawn.
   *
   * Cones under discs under glyphs. This was one flat container with the depth
   * fixed up by juggling `setChildIndex` every frame against a child list that
   * three different things were adding to, which is a race with itself: the
   * discs ended up on top of the glyphs they were supposed to sit behind, so
   * the thing that exists to make an icon readable was hiding it instead.
   *
   * Sub-containers cost nothing and cannot get this wrong.
   */
  private readonly coneLayer = new Container();
  private readonly discLayer = new Container();
  private readonly glyphLayer = new Container();

  private readonly sprites = new Map<string, Sprite>();
  private readonly texture: Texture;
  private readonly icons: Icons;
  /** The direction cone under each camera, keyed the same way. */
  private readonly cones = new Map<string, Sprite>();
  /**
   * The dark disc each camera sits on.
   *
   * So the mark reads on whatever is underneath it. On the dark map an orange
   * glyph is legible on its own; over aerial imagery it lands on concrete and
   * rooftops and disappears. The disc gives it its own ground.
   */
  private readonly discs = new Map<string, Sprite>();
  /** What is over each covered lens, drawn where the ray would have been. */
  private readonly covers = new Map<string, Sprite>();
  private readonly projection: Projection;
  private readonly known: KnownCameras;
  /** The agency's own deployment, while on shift. Held apart from the above. */
  private coverage: Coverage | null = null;
  private enforcement = false;
  private readonly community: CommunityHeld;
  private readonly reported = new Map<number, Sprite>();
  /** Whether other people's reports are drawn at all. */
  showCommunity = false;

  constructor(
    renderer: Renderer,
    projection: Projection,
    known: KnownCameras,
    community: CommunityHeld,
  ) {
    this.projection = projection;
    this.known = known;
    this.community = community;

    // The camera icon, which is a box on a pole -- what one actually looks
    // like. It was an anonymous wedge, twenty-two pixels across, and it read as
    // a cursor artefact rather than as the thing the whole other half of the
    // game is made of.
    this.icons = new Icons(renderer);
    this.container.addChild(this.coneLayer, this.discLayer, this.glyphLayer);

    // And the cone it reads, which is the information that matters once you
    // have found one: a camera watching the other way is not a camera you have
    // to avoid.
    //
    // Its apex is the camera's own position, so the two are the same object
    // rather than a shape with another shape near it. They were near it -- a
    // fat wedge sitting below a side-on icon with a gap between them, which is
    // what a direction marker looks like when nothing decides where it starts.
    //
    // Drawn pointing up, apex at the bottom, so anchoring at (0.5, 1) puts the
    // apex on the position and a rotation of zero looks north.
    const half = Math.tan((CONE_DEGREES / 2) * (Math.PI / 180)) * CONE_LENGTH;
    const cone = new Graphics()
      .moveTo(CONE_LENGTH, CONE_LENGTH)
      .lineTo(CONE_LENGTH - half, 0)
      .lineTo(CONE_LENGTH + half, 0)
      .closePath()
      .fill(0xffffff);
    this.texture = renderer.generateTexture({ target: cone, resolution: 2 });
    cone.destroy();
  }

  /** Give it the agency's coverage to draw alongside what was found. */
  setCoverage(coverage: Coverage): void {
    this.coverage = coverage;
  }

  /**
   * Which half of the game is being played, because they see different maps.
   *
   * On shift you are looking at your department's deployment. Off shift you are
   * looking at what your plate has driven past. Showing both at once was
   * showing an officer a scatter of cameras they had personally spotted while
   * off duty, mixed in with the ones they operate -- two different kinds of
   * knowledge in one picture, which is the distinction §8 spends its length
   * drawing.
   */
  setEnforcement(on: boolean): void {
    this.enforcement = on;
  }

  /**
   * Everything to draw: what this plate found, plus the agency's own.
   *
   * A camera in both is drawn once, as a found one, because having driven past
   * it is the stronger claim and the one that survives signing off.
   */
  private toDraw(): { camera: SightedCamera; operated: boolean }[] {
    const out: { camera: SightedCamera; operated: boolean }[] = [];
    const mine = new Set<string>();
    // On shift, the department's map is the map. What this plate happened to
    // spot while off duty is not part of the job and not what an operator's
    // console shows them.
    if (!this.enforcement) {
      for (const camera of this.known.all()) {
        mine.add(camera.id);
        out.push({ camera, operated: false });
      }
    }
    for (const camera of this.coverage?.all() ?? []) {
      if (mine.has(camera.id)) continue;
      out.push({ camera, operated: true });
    }
    return out;
  }

  update(viewport: Viewport): void {
    const visible = viewport.visibleBounds(120);
    const scale = viewport.scale;
    // Big enough to read as a camera and no bigger.
    //
    // This started at twenty-two pixels, which was small enough to be mistaken
    // for a cursor, and overshot to forty-six on the way back -- a camera that
    // covers the junction it is standing at is not more legible, it is just
    // louder, and there are two hundred of them. These are the most important
    // objects on the map, which is a reason to make them *readable*, not a
    // license to make them large.
    const size = Math.max(11, Math.min(26, 11 * scale * 40));

    const live = new Set<string>();
    for (const { camera, operated } of this.toDraw()) {
      // Bounds are in projected units, so the camera has to be projected before
      // it can be culled against them.
      const p = project(this.projection, camera.x, camera.y);
      if (p.sx < visible.minX || p.sx > visible.maxX || p.sy < visible.minY || p.sy > visible.maxY) {
        continue;
      }
      live.add(camera.id);

      let sprite = this.sprites.get(camera.id);
      if (!sprite) {
        sprite = new Sprite(this.texture);
        sprite.anchor.set(0.5, 0.5);
        this.glyphLayer.addChild(sprite);
        this.sprites.set(camera.id, sprite);
      }

      let disc = this.discs.get(camera.id);
      if (!disc) {
        disc = new Sprite(this.icons.get('disc'));
        disc.anchor.set(0.5, 0.5);
        disc.tint = 0x080b10;
        disc.alpha = 0.72;
        this.discLayer.addChild(disc);
        this.discs.set(camera.id, disc);
      }

      const x = viewport.toScreenX(p.sx);
      const y = viewport.toScreenY(p.sy);
      const down = camera.down === true;
      // Covered means it is reading nothing right now. Dimmed rather than
      // hidden, because the pole is still there and you still have to plan
      // around it coming back.
      //
      // Blue for a camera you operate rather than one you found. An officer
      // looking at their own deployment and a driver looking at what they have
      // spotted are two different pieces of knowledge, and colouring them the
      // same would say they were the same.
      const tint = down ? 0x6b7a8c : operated ? 0x5aa9ff : 0xffb020;

      // The cone it watches, underneath.
      let cone = this.cones.get(camera.id);
      if (!cone) {
        cone = new Sprite(this.texture);
        cone.anchor.set(0.5, 1);
        this.coneLayer.addChild(cone);
        this.cones.set(camera.id, cone);
      }
      cone.position.set(x, y);
      //Long enough to read as a direction without covering the road it watches.
      cone.width = size * 1.9;
      cone.height = size * 1.9;
      // A camera found before the server started computing this has no bearing
      // on record. Better to show no cone than to point it due north.
      cone.visible = camera.b >= 0;
      // Where it looks, foreshortened the way everything else on this tilted map
      // is. The bearing arrives as a compass angle on the flat ground; the map
      // squashes the north-south axis (a 55-degree tilt), so a cone drawn with
      // the raw bearing points a little wrong on anything that is not due
      // east-west -- the same skew the vehicle sprites correct for. The ground
      // direction (sin east, cos north) becomes the screen direction (east,
      // -north * yScale), and the cone, which points up at rotation zero, turns
      // to match.
      const bearing = (camera.b * Math.PI) / 180;
      cone.rotation = Math.atan2(
        Math.sin(bearing),
        Math.cos(bearing) * this.projection.yScale,
      );
      cone.tint = tint;
      /**
       * A covered camera has no ray.
       *
       * It was drawn faint, which said "this one is reading a bit less". It is
       * not reading anything -- that is the whole of what obstruction buys, and
       * a cone at six percent was quietly arguing the opposite. Gone entirely,
       * and what is over the lens goes in the space it left.
       */
      cone.visible = cone.visible && !down;
      cone.alpha = 0.14;

      // And what is over the lens, sitting where the ray would have been.
      let cover = this.covers.get(camera.id);
      const covering = down && camera.downWith !== undefined;
      if (covering && !cover) {
        cover = new Sprite(this.icons.get(camera.downWith as IconName));
        cover.anchor.set(0.5, 0.5);
        this.glyphLayer.addChild(cover);
        this.covers.set(camera.id, cover);
      }
      if (cover) {
        cover.visible = covering;
        if (covering) {
          cover.texture = this.icons.get(camera.downWith as IconName);
          // Out along the bearing the camera looks, at about the distance the
          // ray used to start -- so it reads as being on the lens rather than
          // as a second thing standing near the pole.
          const along = ((camera.b >= 0 ? camera.b : 0) * Math.PI) / 180;
          cover.position.set(x + Math.sin(along) * size * 0.62, y - Math.cos(along) * size * 0.62);
          cover.width = size * 0.72;
          cover.height = size * 0.72;
          cover.tint = 0xffd27a;
        }
      }

      // And the housing, on the same point and turned the same way, so the
      // cone comes out of the lens rather than starting somewhere near it.
      // Seen from above a camera has a front, and the front is the whole
      // point of it.
      sprite.texture = this.icons.get(down ? 'dispatch' : 'camera');
      sprite.position.set(x, y);
      sprite.width = size;
      sprite.height = size;
      sprite.rotation = camera.b >= 0 ? (camera.b * Math.PI) / 180 : 0;
      sprite.tint = tint;
      sprite.alpha = down ? 0.6 : 1;

      // Behind the glyph and in front of the cone, so the cone reads as coming
      // out from under the camera rather than across it.
      disc.position.set(x, y);
      disc.width = size * 1.06;
      disc.height = size * 1.06;
    }

    for (const [id, sprite] of this.sprites) {
      if (live.has(id)) continue;
      sprite.destroy();
      this.sprites.delete(id);
      this.cones.get(id)?.destroy();
      this.cones.delete(id);
      this.discs.get(id)?.destroy();
      this.discs.delete(id);
      this.covers.get(id)?.destroy();
      this.covers.delete(id);
    }

    this.paintCommunity(viewport, visible, size);
  }

  /**
   * Other people's reports -- **only the ones you have not found yourself.**
   *
   * This drew every marker on the community map, including all the ones the
   * player had personally reported, in a dimmer color underneath their own. So
   * turning it on changed nothing anybody could see: every reported camera was
   * one they had reported, and the dim marker sat exactly beneath the bright
   * one.
   *
   * What a crowdsourced map is *for* is the cameras you have not driven past.
   * Those are the only ones drawn here, and that is the whole difference the
   * toggle is supposed to make.
   *
   * Dimmer than your own because they are a weaker claim: somebody said this
   * was here, at some point, and nothing since has necessarily checked. The
   * confidence tier sets how solid it looks, so a player reads the difference
   * between "four people have seen this recently" and "one person mentioned it
   * once" without being told what either means.
   */
  private paintCommunity(
    viewport: Viewport,
    visible: { minX: number; minY: number; maxX: number; maxY: number },
    size: number,
  ): void {
    const live = new Set<number>();
    if (this.showCommunity) {
      // Where this player has already been. A report within this of one of
      // their own cameras is that camera, and drawing it again says nothing.
      const mine = this.known.all();
      const near = (x: number, y: number): boolean =>
        mine.some((c) => Math.hypot(c.x - x, c.y - y) < SAME_CAMERA_METERS);

      let index = 0;
      for (const marker of this.community.all()) {
        if (near(marker.x, marker.y)) {
          index++;
          continue;
        }
        const p = project(this.projection, marker.x, marker.y);
        if (
          p.sx < visible.minX ||
          p.sx > visible.maxX ||
          p.sy < visible.minY ||
          p.sy > visible.maxY
        ) {
          index++;
          continue;
        }
        const key = index++;
        live.add(key);

        let sprite = this.reported.get(key);
        if (!sprite) {
          sprite = new Sprite(this.icons.get('camera'));
          sprite.anchor.set(0.5, 0.5);
          this.discLayer.addChild(sprite);
          this.reported.set(key, sprite);
        }
        sprite.position.set(viewport.toScreenX(p.sx), viewport.toScreenY(p.sy));
        sprite.width = size;
        sprite.height = size;
        sprite.rotation = 0;
        // Somebody else's report, in a color that is plainly not yours.
        sprite.tint = 0x7fa6d9;
        sprite.alpha = CONFIDENCE_ALPHA[marker.c];
      }
    }

    for (const [key, sprite] of this.reported) {
      if (live.has(key)) continue;
      sprite.destroy();
      this.reported.delete(key);
    }
  }
}

/**
 * How solid a reported marker looks, by how well established it is.
 *
 * Not decoration. A player deciding whether to trust somebody else's marker is
 * doing, in miniature, what the archive does with a sighting versus a permit,
 * and a visible gradient teaches that distinction better than a legend would.
 */
/**
 * How close two positions have to be to be the same camera.
 *
 * A crowdsourced position is the average of what several people said, so it
 * lands a little off the pole rather than on it. Twenty-five meters is wide
 * enough to recognize a camera you already have and tight enough not to swallow
 * the next one along -- the real deployment's nearest-neighbor median is 134 m.
 */
const SAME_CAMERA_METERS = 25;

const CONFIDENCE_ALPHA: Record<CommunityMarker['c'], number> = {
  'field-verified': 0.75,
  persistent: 0.55,
  corroborated: 0.4,
  unconfirmed: 0.22,
};
